/**
 * The file-explorer selection model, mirroring desktop file managers.
 *
 * Plain click replaces the selection · Ctrl/Cmd toggles one · Shift selects the
 * contiguous range from the anchor. Three pieces of state make that work and
 * they only make sense together:
 *
 * - `selectedUuids` — what is selected (rows' `uuid`s)
 * - `anchorUuid`    — the pivot a Shift-range extends FROM
 * - `focusUuid`     — where the keyboard is, which is not the same thing
 *
 * The distinction between selected and focused is easy to lose: the UI draws
 * both with a primary ring (selection adds an offset), so a bug that conflates
 * them looks almost right.
 *
 * Selection is capped, because every bulk action operates on it — capping here
 * caps delete, move and copy in one place.
 */

import { useCallback, useEffect, useState } from 'react';

export const MAX_SELECTION = 100;

/**
 * @param items        the ordered rows currently listed — Shift-ranges are
 *                     computed against this order, so it must match what is on
 *                     screen, not the unsorted response
 * @param resetKey     changing this clears the selection; pass whatever
 *                     identifies the current dataset (location + folder)
 * @param onCapped     called when a selection would exceed the cap
 * @param containerRef the scrollable list element. Selecting with the mouse
 *                     returns keyboard focus to it, so arrow keys keep working
 *                     after a click — without this, click-then-arrow is dead.
 */
export function useNodeSelection({ items = [], resetKey, onCapped, containerRef } = {}) {
  const [selectedUuids, setSelectedUuids] = useState(() => new Set());
  const [anchorUuid, setAnchorUuid] = useState(null);
  const [focusUuid, setFocusUuid] = useState(null);

  const clearSelection = useCallback(() => {
    setSelectedUuids(new Set());
    setAnchorUuid(null);
    setFocusUuid(null);
  }, []);

  // Switching location or folder is a move to a DIFFERENT dataset, so a
  // selection carried over from the previous one is meaningless — and acting
  // on it in bulk would touch rows the user can no longer see.
  useEffect(() => {
    clearSelection();
  }, [resetKey, clearSelection]);

  const orderedIndex = useCallback(
    (uuid) => items.findIndex((item) => item.uuid === uuid),
    [items],
  );

  const toggleSelect = useCallback(
    (uuid) => {
      // The cap is checked against current state rather than inside the
      // updater: an updater runs later, so a flag set in there is still false
      // when the caller reads it and the warning never fires.
      if (!selectedUuids.has(uuid) && selectedUuids.size >= MAX_SELECTION) {
        onCapped?.();
        return;
      }
      setSelectedUuids((previous) => {
        const next = new Set(previous);
        if (next.has(uuid)) next.delete(uuid);
        else next.add(uuid);
        return next;
      });
      setAnchorUuid(uuid);
      setFocusUuid(uuid);
    },
    [selectedUuids, onCapped],
  );

  /** Select anchor→target inclusive. `additive` keeps what was already there. */
  const applyRange = useCallback(
    (fromUuid, toUuid, additive) => {
      const from = orderedIndex(fromUuid);
      const to = orderedIndex(toUuid);
      if (from < 0 || to < 0) return;
      const [low, high] = from <= to ? [from, to] : [to, from];

      const next = additive ? new Set(selectedUuids) : new Set();
      let capped = false;
      for (const item of items.slice(low, high + 1)) {
        if (next.size >= MAX_SELECTION) {
          capped = true;
          break;
        }
        next.add(item.uuid);
      }
      setSelectedUuids(next);
      if (capped) onCapped?.();
    },
    [items, orderedIndex, selectedUuids, onCapped],
  );

  /** The click handler a row binds to. */
  const selectRow = useCallback(
    (event, item) => {
      const { uuid } = item;
      const additive = event.metaKey || event.ctrlKey;

      if (event.shiftKey && anchorUuid != null) {
        applyRange(anchorUuid, uuid, additive);
        setFocusUuid(uuid);
      } else if (additive) {
        toggleSelect(uuid);
      } else {
        setSelectedUuids(new Set([uuid]));
        setAnchorUuid(uuid);
        setFocusUuid(uuid);
      }

      // Keep keystrokes flowing to the list after a click. Clicking a row
      // moves DOM focus to the row itself, so without this the container's
      // keydown handler stops firing and arrow-key navigation is dead.
      containerRef?.current?.focus({ preventScroll: true });
    },
    [anchorUuid, applyRange, toggleSelect, containerRef],
  );

  const toggleSelectAll = useCallback(
    (rows) => {
      const allSelected = rows.length > 0 && rows.every((row) => selectedUuids.has(row.uuid));
      if (allSelected) {
        clearSelection();
        return;
      }
      const next = new Set();
      let capped = false;
      for (const row of rows) {
        if (next.size >= MAX_SELECTION) {
          capped = true;
          break;
        }
        next.add(row.uuid);
      }
      setSelectedUuids(next);
      if (capped) onCapped?.();
    },
    [selectedUuids, clearSelection, onCapped],
  );

  return {
    selectedUuids,
    setSelectedUuids,
    anchorUuid,
    setAnchorUuid,
    focusUuid,
    setFocusUuid,
    selectionCount: selectedUuids.size,
    isSelected: (uuid) => selectedUuids.has(uuid),
    selectRow,
    toggleSelect,
    toggleSelectAll,
    applyRange,
    clearSelection,
    orderedIndex,
  };
}
